import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, finalize, map, of, take } from 'rxjs';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { FilterDataType } from '../../../models/core/table/FilterDataType';
import { QueryBuilderParams } from '../../../models/core/table/QueryBuilderParams';
import { TableActionButton } from '../../../models/core/table/TableActionButton';
import { TableColumn } from '../../../models/core/table/TableColumn';
import { TableEvent } from '../../../models/core/table/TableEvent';
import { TableSearchEventData } from '../../../models/core/table/TableSearchEventData';
import { Tenant } from '../../../models/domain/Tenant';
import { EventTypes } from '../../../models/enums/EventTypes';
import { GroupType } from '../../../models/enums/GroupType';
import { ModalText } from '../../../models/enums/ModalText';
import { LoadingService } from '../../../services/loading.service';
import { ModalService } from '../../../services/modal.service';
import { RoleService } from '../../../services/roleService';
import { TenantService } from '../../../services/tenant.service';
import { getSearchOperator } from '../../../utilities/common-functions';
import { UrlHelper } from '../../../utilities/UrlHelper';

import { TableComponent } from '../../../core/table/table.component';
// Update the import path to the correct location of CreateB2CClient
import { SearchExpression } from '../../../models/core/table/SearchExpression';
import { Address } from '../../../models/domain/Address';
import { CreateB2CClient } from '../../../models/domain/B2CClient/CreateB2CClient';
import { RoleEnum } from '../../../models/enums/RoleEnum';
import { AuthenticationService } from '../../../services/authentication.service';
import { B2CClientDetailsComponent } from '../b2cclient-details/b2cclient-details.component';
import { TableCellTypeEnum } from '../../../models/enums/TableCellTypeEnum';

@Component({
    selector: 'app-b2cclient-list',
    imports: [RouterLink, TableComponent, B2CClientDetailsComponent, NgIf],
    templateUrl: './b2cclient-list.component.html',
    styleUrl: './b2cclient-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class B2CClientListComponent {
    //#region Table Variables
    public columns!: TableColumn[];
    public actions!: TableActionButton[];
    public totalRecords = 0;
    public pageIndex = 0;
    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 50, 100];
    public searchOptions!: BaseSearchOptions;
    //#endregion

    userId = 0;
    createb2cclient!: CreateB2CClient;
    //createb2cclients$: Observable<CreateB2CClient[]> = new Observable<CreateB2CClient[]>();
    public createb2cclientsSubject = new BehaviorSubject<CreateB2CClient[]>([]);
    createb2cclients$ = this.createb2cclientsSubject.asObservable();

    // Popup Trigger
    classApplied = false;
    isToggled = false;
    showModal = false;
    deleteModalData!: Tenant;
    ConfirmText = ModalText.ConfirmYes;
    CancelText = ModalText.ConfirmNo;

    // Added new properties for image popup
    showImageModal = false;
    selectedImageUrl = '';
    selectedImageTitle = '';
    //end

    constructor(
        public themeService: CustomizerSettingsService,
        public tenantService: TenantService,
        private modalService: ModalService,
        private toasterService: ToastrService,
        private roleService: RoleService,
        private loadingService: LoadingService,
        private authService: AuthenticationService,
    ) {
        this.initialize();
    }

    ngOnInit(): void {
        this.getData();
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });

        this.searchOptions = new BaseSearchOptions(
            undefined,
            'id',
            0,
            this.pageIndex,
            this.pageSize,
        );
        this.buildTable();
    }

    private getData(): void {
        this.loadingService.showLoading();

        this.authService
            .getB2cClient(this.searchOptions)
            .pipe(
                take(1),
                map((result) => {
                    const body = result.body ?? { values: [], totalCount: 0 };
                    this.totalRecords = body.totalCount;

                    body.values.forEach((item) => {
                        item.address = item.address ?? new Address();
                    });

                    // Push result to BehaviorSubject
                    this.createb2cclientsSubject.next(body.values);
                }),
                catchError((error) => {
                    if (error && error.error) {
                        if (error.error.isCustom) {
                            this.toasterService.warning(error.error.message);
                        } else {
                            this.toasterService.error('Something went wrong!');
                        }
                    } else {
                        this.toasterService.error('Something went wrong!');
                    }

                    this.totalRecords = 0;
                    // Push empty array so table clears
                    this.createb2cclientsSubject.next([]);
                    return of(null); // swallow the error
                }),
                finalize(() => {
                    this.loadingService.hideLoading();
                }),
            )
            .subscribe();
    }

    public buildTable(): void {
        this.columns = [
            new TableColumn(
                'Client.Name',
                ' ClientName',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.clientName}`,
                true,
                { tooltip: 'CreateB2CClient clientName' },
            ),
            new TableColumn(
                'Tenant.Name',
                ' TenantName',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.tenantName}`,
                true,
                { tooltip: 'CreateB2CClient tenantName' },
            ),
            new TableColumn(
                'Tenant.Logo',
                'Logo',
                FilterDataType.String,
                false,
                false,
                (data: CreateB2CClient) => this.getImageUrl(data?.logoUrl ?? ''),
                false,
                {
                    tooltip: 'Logo',
                    styleExpression: 'padding:5px',
                    onImageClick: (_event: MouseEvent, data: CreateB2CClient) =>
                        this.onImageClick(_event, data),
                },
                TableCellTypeEnum.IMAGE,
            ),
            new TableColumn(
                'firstName',
                ' FirstName',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.firstName}`,
                true,
                { tooltip: 'CreateB2CClient firstName' },
            ),
            new TableColumn(
                'lastName',
                ' LastName',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.lastName}`,
                true,
                { tooltip: 'CreateB2CClient lastName' },
            ),
            new TableColumn(
                'email',
                ' Email',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.email}`,
                true,
                { tooltip: 'CreateB2CClient email' },
            ),
            new TableColumn(
                'PhoneNumber',
                ' Phone',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.phone}`,
                true,
                { tooltip: 'CreateB2CClient Phone' },
                undefined,
                undefined,
                undefined,
                undefined,
                SearchExpression.Equals,
            ),
            new TableColumn(
                'Tenant.Address.AddressLine1',
                ' AddressLine1',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.address?.addressLine1}`,
                true,
                { tooltip: 'CreateB2CClient addressLine1' },
            ),
            new TableColumn(
                'Tenant.Address.AddressLine2',
                ' AddressLine2',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.address?.addressLine2}`,
                true,
                { tooltip: 'CreateB2CClient addressLine2' },
            ),
            new TableColumn(
                'Tenant.Address.PinCode',
                ' PinCode',
                FilterDataType.Int,
                true,
                false,
                (data: CreateB2CClient) => `${data?.address?.pinCode}`,
                true,
                { tooltip: 'CreateB2CClient pinCode' },
                undefined,
                undefined,
                undefined,
                undefined,
                SearchExpression.Equals,
            ),
            new TableColumn(
                'Tenant.Address.State.Name',
                ' State',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.address?.state?.name}`,
                true,
                { tooltip: 'CreateB2CClient state' },
            ),
            new TableColumn(
                'Tenant.Address.City.Name',
                ' City',
                FilterDataType.String,
                true,
                false,
                (data: CreateB2CClient) => `${data?.address?.city?.name}`,
                true,
                { tooltip: 'CreateB2CClient city' },
            ),
        ];

        this.actions = [];
        if (this.authService.hasRoles([RoleEnum.ClientSuperAdmin])) {
            this.actions.push(
                new TableActionButton(
                    'edit',
                    (event, value) => this.onEditClick(event, value),
                    (value) => this.onDisabledClick(),
                    'Edit',
                    'Edit',
                ),
                new TableActionButton(
                    'delete',
                    (event, value) => this.onDeleteClick(event, value),
                    (value) => this.onDisabledClick(),
                    'Delete',
                    'Delete',
                ),
            );
        }
    }

    public onAddClick(): void {
        this.createb2cclient = new CreateB2CClient();
        this.toggleClass();
    }

    public onEditClick(event: MouseEvent, data: CreateB2CClient): void {
        event.stopPropagation();
        this.createb2cclient = data;
        this.toggleClass();
    }

    public onDeleteClick(event: MouseEvent, data: CreateB2CClient): void {
        event.stopPropagation();
        this.modalService.openModal(
            'Delete Confirmation',
            `Are you sure you want to delete <strong> ${data.clientName} </strong> ?`,
            ModalText.ConfirmYes,
            ModalText.ConfirmNo,
        );

        this.modalService.onConfirm.subscribe((isConfirm) => {
            if (isConfirm && data.id) {
                this.loadingService.showLoading();
                this.authService
                    .deleteB2cClient(data.id)
                    .pipe(
                        take(1),
                        catchError((error) => {
                            if (error && error.error) {
                                if (error.error.isCustom) {
                                    this.toasterService.warning(
                                        error.error.message,
                                    );
                                } else {
                                    this.toasterService.error(
                                        'Something went wrong!',
                                    );
                                }
                            } else {
                                this.toasterService.error(
                                    'Something went wrong!',
                                );
                            }
                            return of(null);
                        }),
                    )
                    .subscribe((result) => {
                        if (result?.body) {
                            this.toasterService.info(
                                'Record delete successfully.',
                            );
                            this.getData();
                        }
                        this.loadingService.hideLoading();
                    });
            }
        });
    }

    public onDisabledClick(): boolean {
        return false;
    }

    public tableEvent(event: TableEvent<any>): void {
        if (event.type === EventTypes.TABLE) {
            const eventValue = event.data as TableSearchEventData;

            this.searchOptions.updateByObject(eventValue.baseSearchOptions);

            if (eventValue.columnFilterValues) {
                this.createFilter(eventValue);
            } else {
                this.searchOptions.filter = '';
            }
            this.getData();
        }
        if (event.type === EventTypes.CLEAR) {
            this.clearFilters();
            this.getData();
        }
        if (event.type === EventTypes.IMAGECLICK) {
            const { row } = event.data;
            this.onImageClick(event.data.event, row);
        }
    }

    public clearFilters(): void {
        this.pageIndex = 0;
        this.pageSize = 10;
        this.searchOptions = new BaseSearchOptions(
            undefined,
            'id',
            0,
            this.pageIndex,
            this.pageSize,
        );
    }

    private createFilter(eventValue: TableSearchEventData): void {
        const queryParams: QueryBuilderParams[] = [];

        for (const [
            columnName,
            { dataType, opType, searchValue },
        ] of Object.entries(eventValue.columnFilterValues)) {
            const searchOperator = getSearchOperator(opType.toString());
            if (!searchOperator) continue; // Skip if no valid operator

            queryParams.push({
                fieldName: columnName, // Adjust as necessary for your field mapping
                dataType: dataType,
                searchOperator,
                searchValue: searchValue,
                groupType: GroupType.OR,
            });
        }

        this.searchOptions.filter =
            this.searchOptions.generateQuery(queryParams);
    }

    public toggleClass(): void {
        this.classApplied = !this.classApplied;
    }

    public classAppliedEvent(flag: boolean): void {
        this.classApplied = flag;
    }

    public saveConfirmationEvent(isSaved: boolean): void {
        if (isSaved) {
            this.getData();
        }
    }

    public getImageUrl(path: string): string {
        return UrlHelper.getImageUrl(path);
    }

    public onImageClick(_event: MouseEvent, data: CreateB2CClient): void {
        this.selectedImageUrl = UrlHelper.getImageUrl(data?.logoUrl ?? '');
        this.selectedImageTitle = `${data?.clientName} - ${data?.tenantName}`;
        this.showImageModal = true;
    }

    public closeImageModal(): void {
        this.showImageModal = false;
        this.selectedImageUrl = '';
        this.selectedImageTitle = '';
    }
}
